'use client';

import { useState, useEffect } from 'react';
import { ZoomIn, X, ChevronLeft, ChevronRight, Award, ShieldCheck } from 'lucide-react';

interface PayoutCert {
  id: number | string;
  image_url?: string | null;
  trader_name?: string | null;
  amount?: number;
  account_size?: number;
  split_percent?: number;
  wallet_masked?: string;
  date?: string;
  certificate_no?: string;
}

interface PayoutCertificatesSectionProps {
  lang: string;
  payouts?: any[];
}

export default function PayoutCertificatesSection({ lang, payouts }: PayoutCertificatesSectionProps) {
  const isFa = lang === 'fa';

  // Fallback certificates with generated SVG certificate artwork if no custom images uploaded yet
  const defaultCerts: PayoutCert[] = [
    {
      id: 1,
      image_url: null,
      trader_name: isFa ? 'علی ر.' : 'Ali R.',
      amount: 8940,
      account_size: 100000,
      split_percent: 90,
      wallet_masked: 'TKn7xP...4E1a',
      date: isFa ? 'دیروز' : 'Yesterday',
      certificate_no: 'PF-2026-90412'
    },
    {
      id: 2,
      image_url: null,
      trader_name: isFa ? 'رضا م.' : 'Reza M.',
      amount: 4320,
      account_size: 50000,
      split_percent: 85,
      wallet_masked: 'TLm9aQ...8F3c',
      date: isFa ? '۲ روز قبل' : '2 days ago',
      certificate_no: 'PF-2026-87231'
    },
    {
      id: 3,
      image_url: null,
      trader_name: isFa ? 'محمد ک.' : 'Mohammad K.',
      amount: 15850,
      account_size: 200000,
      split_percent: 90,
      wallet_masked: 'TRv3zK...9B2d',
      date: isFa ? '۳ روز قبل' : '3 days ago',
      certificate_no: 'PF-2026-78904'
    },
    {
      id: 4,
      image_url: null,
      trader_name: isFa ? 'سارا ب.' : 'Sara B.',
      amount: 2180,
      account_size: 25000,
      split_percent: 80,
      wallet_masked: 'TPw8xY...1A9e',
      date: isFa ? 'هفته جاری' : 'This week',
      certificate_no: 'PF-2026-65419'
    },
    {
      id: 5,
      image_url: null,
      trader_name: isFa ? 'فرهاد ط.' : 'Farhad T.',
      amount: 3950,
      account_size: 50000,
      split_percent: 85,
      wallet_masked: 'TXd2sL...7C4f',
      date: isFa ? 'هفته جاری' : 'This week',
      certificate_no: 'PF-2026-54128'
    },
    {
      id: 6,
      image_url: null,
      trader_name: isFa ? 'امیرحسین ن.' : 'Amir H.',
      amount: 7600,
      account_size: 100000,
      split_percent: 90,
      wallet_masked: 'TCq5vM...3D8b',
      date: isFa ? 'هفته جاری' : 'This week',
      certificate_no: 'PF-2026-43187'
    }
  ];

  // Process payouts: merge with defaults
  const certificates: PayoutCert[] = (payouts && payouts.length > 0)
    ? payouts.map((p, idx) => ({
        id: p.id || idx,
        image_url: p.image_url || null,
        trader_name: p.trader_name || (isFa ? 'معامله‌گر ریل' : 'Funded Trader'),
        amount: p.amount || 0,
        account_size: p.account_size || 50000,
        split_percent: p.split_percent || 90,
        wallet_masked: p.wallet_masked || 'USDT TRC20',
        date: p.date || (isFa ? 'اخیراً' : 'Recently'),
        certificate_no: p.certificate_no || `PF-2026-${String(p.id || idx).padStart(5, '0')}`
      }))
    : defaultCerts;

  // Lightbox Modal state
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Close on ESC key or arrow navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowRight' && selectedIndex !== null) {
        setSelectedIndex(prev => (prev !== null && prev > 0 ? prev - 1 : certificates.length - 1));
      }
      if (e.key === 'ArrowLeft' && selectedIndex !== null) {
        setSelectedIndex(prev => (prev !== null && prev < certificates.length - 1 ? prev + 1 : 0));
      }
    };
    if (selectedIndex !== null) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedIndex, certificates.length]);

  const activeCert = selectedIndex !== null ? certificates[selectedIndex] : null;

  return (
    <section className="home-payouts-section" style={{ width: '100%', maxWidth: '1200px', margin: '60px auto 40px auto', boxSizing: 'border-box' }}>
      {/* Section Header */}
      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <div 
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '8px', 
            backgroundColor: 'rgba(16, 185, 129, 0.08)', 
            border: '1px solid rgba(16, 185, 129, 0.25)', 
            color: '#059669', 
            padding: '6px 18px', 
            borderRadius: '30px', 
            fontSize: '13px', 
            fontWeight: '700',
            marginBottom: '14px'
          }}
        >
          <span style={{ width: '8px', height: '8px', backgroundColor: '#10b981', borderRadius: '50%' }}></span>
          {isFa ? 'شفافیت مالی و برداشت‌های واقعی' : 'Verified Trader Payouts'}
        </div>

        <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: '900', color: '#0f172a', margin: '0 0 12px 0', letterSpacing: '-0.5px' }}>
          {isFa ? 'گواهی برداشت‌های ' : 'Recent Trader '}
          <span className="gradient-text">{isFa ? 'معامله‌گران پس‌فاند' : 'Payout Certificates'}</span>
        </h2>

        <p style={{ color: '#64748b', fontSize: '15px', maxWidth: '640px', margin: '0 auto', lineHeight: '1.8' }}>
          {isFa 
            ? 'تصاویر واقعی و رسمی گواهی‌های تسویه سود معامله‌گران پس‌فاند. برای مشاهده تصویر کامل و بزرگنمایی روی هر گواهی کلیک کنید.' 
            : 'Explore real official profit payout certificates delivered to our funded traders. Click any certificate to inspect full resolution.'}
        </p>
      </div>

      {/* Certificates Gallery Grid - Light Theme */}
      <div 
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
          gap: '24px',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {certificates.map((cert, idx) => (
          <div
            key={cert.id}
            onClick={() => setSelectedIndex(idx)}
            className="certificate-card group"
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '20px',
              padding: '16px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-6px)';
              e.currentTarget.style.borderColor = '#0ea5e9';
              e.currentTarget.style.boxShadow = '0 16px 36px rgba(14, 165, 233, 0.16), 0 0 20px rgba(14, 165, 233, 0.12)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
            }}
          >
            {/* Top Bar inside card */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '0 4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span 
                  style={{ 
                    backgroundColor: '#f1f5f9', 
                    color: '#475569', 
                    border: '1px solid #cbd5e1', 
                    padding: '3px 8px', 
                    borderRadius: '6px', 
                    fontSize: '11px', 
                    fontWeight: '700',
                    fontFamily: 'monospace'
                  }}
                >
                  {cert.certificate_no || `PF-2026-${String(cert.id).padStart(5, '0')}`}
                </span>
                <span 
                  style={{ 
                    backgroundColor: 'rgba(16, 185, 129, 0.1)', 
                    color: '#059669', 
                    border: '1px solid rgba(16, 185, 129, 0.25)', 
                    padding: '3px 8px', 
                    borderRadius: '6px', 
                    fontSize: '11px', 
                    fontWeight: '700' 
                  }}
                >
                  ✓ {isFa ? 'تایید شده' : 'Verified'}
                </span>
              </div>

              {cert.amount && cert.amount > 0 ? (
                <div style={{ fontSize: '14px', fontWeight: '800', color: '#0284c7', direction: 'ltr' }}>
                  ${cert.amount.toLocaleString()} <span style={{ fontSize: '11px', color: '#64748b' }}>USDT</span>
                </div>
              ) : null}
            </div>

            {/* Certificate Image Frame */}
            <div 
              style={{ 
                position: 'relative', 
                width: '100%', 
                aspectRatio: '16 / 10', 
                borderRadius: '14px', 
                overflow: 'hidden', 
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e2e8f0'
              }}
            >
              {cert.image_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img 
                  src={cert.image_url} 
                  alt={cert.trader_name || 'Certificate'} 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                /* Fallback Graphic Certificate - Light Theme with Site Blue Accents */
                <div 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f0f9ff 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    textAlign: 'center',
                    border: '2px solid rgba(14, 165, 233, 0.3)',
                    borderRadius: '12px',
                    position: 'relative'
                  }}
                >
                  <div style={{ 
                    width: '46px', 
                    height: '46px', 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(14, 165, 233, 0.1)', 
                    border: '2px solid #0ea5e9', 
                    color: '#0284c7', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    marginBottom: '8px' 
                  }}>
                    <Award size={24} />
                  </div>
                  <div style={{ fontSize: '11px', letterSpacing: '1px', color: '#0284c7', fontWeight: '800', textTransform: 'uppercase' }}>
                    PASSFUND PAYOUT CERTIFICATE
                  </div>
                  <div style={{ fontSize: '17px', fontWeight: '900', color: '#0f172a', marginTop: '4px' }}>
                    {cert.trader_name}
                  </div>
                  {cert.amount && cert.amount > 0 && (
                    <div style={{ fontSize: '19px', fontWeight: '900', color: '#0284c7', marginTop: '4px', direction: 'ltr' }}>
                      ${cert.amount.toLocaleString()} USDT
                    </div>
                  )}
                </div>
              )}

              {/* Hover Zoom Overlay */}
              <div 
                style={{ 
                  position: 'absolute', 
                  inset: 0, 
                  backgroundColor: 'rgba(15, 23, 42, 0.45)', 
                  opacity: 0, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '8px', 
                  color: '#ffffff', 
                  transition: 'opacity 0.25s ease', 
                  backdropFilter: 'blur(3px)' 
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
              >
                <div style={{ 
                  width: '46px', 
                  height: '46px', 
                  borderRadius: '50%', 
                  backgroundColor: '#0ea5e9', 
                  color: '#ffffff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)'
                }}>
                  <ZoomIn size={22} />
                </div>
                <span style={{ fontSize: '12px', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                  {isFa ? 'مشاهده تصویر بزرگ' : 'Enlarge Image'}
                </span>
              </div>
            </div>

            {/* Bottom Caption */}
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
              <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                {cert.trader_name || (isFa ? 'گواهی برداشت' : 'Certificate')}
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: '#64748b', 
                backgroundColor: '#f1f5f9', 
                padding: '3px 8px', 
                borderRadius: '6px',
                fontWeight: '600'
              }}>
                {cert.date || (isFa ? 'تسویه شده' : 'Settled')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fullscreen Lightbox Modal */}
      {activeCert && (
        <div 
          onClick={() => setSelectedIndex(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            padding: '20px'
          }}
        >
          {/* Lightbox Content Container */}
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ 
              position: 'relative', 
              maxWidth: '92vw', 
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.7), 0 0 40px rgba(14, 165, 233, 0.18)',
              border: '1px solid rgba(14, 165, 233, 0.35)',
              backgroundColor: '#0a0f1d'
            }}
          >
            {/* Top Bar of Modal */}
            <div 
              style={{ 
                width: '100%', 
                backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
                padding: '12px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxSizing: 'border-box'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Award size={20} style={{ color: '#0ea5e9' }} />
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffffff' }}>
                  {activeCert.trader_name}
                </span>
                <span style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                  {activeCert.certificate_no}
                </span>
                {activeCert.amount && activeCert.amount > 0 && (
                  <span style={{ backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>
                    ${activeCert.amount.toLocaleString()} USDT
                  </span>
                )}
              </div>

              <button
                onClick={() => setSelectedIndex(null)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '34px',
                  height: '34px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.8)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')}
              >
                <X size={18} />
              </button>
            </div>

            {/* Main Image Display */}
            <div style={{ position: 'relative', maxHeight: '80vh', maxWidth: '90vw', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
              {activeCert.image_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img 
                  src={activeCert.image_url} 
                  alt={activeCert.trader_name || 'Certificate'} 
                  style={{ maxWidth: '88vw', maxHeight: '76vh', objectFit: 'contain', borderRadius: '10px' }} 
                />
              ) : (
                /* Fallback Light Royal Certificate inside Modal - Theme Blue */
                <div 
                  style={{ 
                    width: '640px', 
                    maxWidth: '85vw', 
                    height: '420px', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                    borderRadius: '16px',
                    border: '3px solid #0ea5e9',
                    padding: '36px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#0f172a',
                    boxShadow: '0 20px 50px rgba(14, 165, 233, 0.15)'
                  }}
                >
                  <div style={{ width: '68px', height: '68px', borderRadius: '50%', backgroundColor: 'rgba(14, 165, 233, 0.12)', border: '2px solid #0ea5e9', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <Award size={34} />
                  </div>
                  <div style={{ fontSize: '13px', letterSpacing: '2px', color: '#0284c7', fontWeight: '800' }}>
                    PASSFUND PROPFIRM • OFFICIAL PAYOUT CERTIFICATE
                  </div>
                  <div style={{ fontSize: '26px', fontWeight: '900', color: '#0f172a', marginTop: '12px' }}>
                    {activeCert.trader_name}
                  </div>
                  {activeCert.amount && activeCert.amount > 0 && (
                    <div style={{ fontSize: '34px', fontWeight: '900', color: '#0284c7', marginTop: '10px', direction: 'ltr' }}>
                      ${activeCert.amount.toLocaleString()} USDT
                    </div>
                  )}
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '12px', fontWeight: '500' }}>
                    {activeCert.wallet_masked} • {activeCert.certificate_no}
                  </div>
                </div>
              )}

              {/* Prev / Next Nav Buttons */}
              {certificates.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIndex(prev => (prev !== null && prev > 0 ? prev - 1 : certificates.length - 1));
                    }}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(15, 23, 42, 0.85)',
                      color: '#ffffff',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      width: '44px',
                      height: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backdropFilter: 'blur(4px)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0ea5e9')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.85)')}
                  >
                    <ChevronRight size={24} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIndex(prev => (prev !== null && prev < certificates.length - 1 ? prev + 1 : 0));
                    }}
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(15, 23, 42, 0.85)',
                      color: '#ffffff',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      width: '44px',
                      height: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backdropFilter: 'blur(4px)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0ea5e9')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.85)')}
                  >
                    <ChevronLeft size={24} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
